import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { MdAdd, MdBusiness, MdPersonAddAlt1 } from "react-icons/md";
import { CalcOption } from "../../../../types/budget/budget";
import SupplierAutoComplete from "../../SupplierAutoComplete";
import { isTemporarySupplierOption } from "../../../../utils/budget-supplier-id";
import {
  CALC_OPTION_SHORT_LABEL,
  COLORS,
  ChoiceCard,
  ConsequenceList,
  Highlight,
  InfoBox,
  ModalCloseBar,
  ModalHero,
  SectionLabel,
  SuccessStep,
  SummaryBlock,
  SummaryRow,
  WarningBox,
  WarningHero,
  cancelButtonSx,
  modalActionsSx,
  primaryButtonSx,
} from "../CategoryMoveModals/parts";

type Step = "mode" | "pick" | "confirm" | "done";
type Mode = "existing" | "new";

export type TransferTarget = {
  /** `_id` real do fornecedor escolhido (sem sufixos). */
  realSupplierId: string;
  /** Id interno já existente na `supplierList` — só no modo "existing". */
  existingSupplierId?: string;
  supplierName: string;
  isNew: boolean;
  /** "Fornecedor a definir": pode repetir no mesmo bloco (sufixo próprio). */
  isTemporary?: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  categoryLabel: string;
  currentSupplierLabel: string;
  /** `_id` real do fornecedor de origem — bloqueia a seleção dele na busca. */
  currentSupplierRealId: string;
  calcOption: CalcOption;
  /** Fornecedores já cadastrados NESTA forma de cobrança, exceto o atual. */
  existingSuppliers: Array<{ supplierId: string; realId: string; label: string }>;
  onConfirm: (target: TransferTarget) => Promise<boolean>;
};

const CONSEQUENCES = [
  "Todos os serviços, valores, taxas, impostos, observações e configurações da categoria serão transferidos.",
  "Os totais do fornecedor de origem, do fornecedor de destino e do orçamento serão recalculados.",
  "A ação será registrada na auditoria.",
];

const TransferCategorySupplierModal: React.FC<Props> = ({
  open,
  onClose,
  categoryLabel,
  currentSupplierLabel,
  currentSupplierRealId,
  calcOption,
  existingSuppliers,
  onConfirm,
}: Props) => {
  const [step, setStep] = useState<Step>("mode");
  const [mode, setMode] = useState<Mode | "">("");
  const [existingId, setExistingId] = useState("");
  const [newOption, setNewOption] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const hasExistingSuppliers = existingSuppliers.length > 0;
  const calcLabel = CALC_OPTION_SHORT_LABEL[calcOption];

  useEffect(() => {
    if (open) {
      setStep("mode");
      // Sem outro fornecedor na forma de cobrança, "Fornecedor existente" fica
      // indisponível — já abre com "Novo fornecedor" pré-selecionado.
      setMode(hasExistingSuppliers ? "" : "new");
      setExistingId("");
      setNewOption(null);
      setSaving(false);
    }
  }, [open, hasExistingSuppliers]);

  const target: TransferTarget | null = useMemo(() => {
    if (mode === "existing") {
      const found = existingSuppliers.find((s) => s.supplierId === existingId);
      if (!found) return null;
      return {
        realSupplierId: found.realId,
        existingSupplierId: found.supplierId,
        supplierName: found.label,
        isNew: false,
      };
    }
    if (mode === "new" && newOption?._id) {
      return {
        realSupplierId: newOption._id,
        supplierName: newOption.name || "",
        isNew: true,
        isTemporary: isTemporarySupplierOption(newOption),
      };
    }
    return null;
  }, [mode, existingId, newOption, existingSuppliers]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!target) return;
    setSaving(true);
    const ok = await onConfirm(target);
    setSaving(false);
    // Em caso de falha o handler já notificou o usuário; mantém o passo de
    // confirmação aberto para ele poder tentar de novo.
    if (ok) setStep("done");
  };

  // Não é exibido: nomeia a janela para leitores de tela (aria-label).
  const title =
    step === "done"
      ? "Transferência realizada"
      : step === "confirm"
      ? "Confirmar transferência"
      : "Transferir categoria para outro fornecedor";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-label={title}
    >
      <ModalCloseBar onClose={handleClose} />

      {/* `dividers` desenha borda em cima e embaixo; a de cima separava o
          "fechar" do conteúdo e foi removida. */}
      <DialogContent dividers sx={{ px: 3, py: 2.5, borderTop: "none" }}>
        {step === "done" && (
          <SuccessStep
            message={
              <>
                Categoria <Highlight>{categoryLabel}</Highlight> transferida com
                sucesso para o fornecedor{" "}
                <Highlight>{target?.supplierName}</Highlight>.
              </>
            }
            detail="Os totais do orçamento foram atualizados. A ação foi registrada na auditoria."
          />
        )}

        {step === "confirm" && (
          <>
            <WarningHero>
              Deseja transferir a categoria{" "}
              <Highlight>{categoryLabel}</Highlight> do fornecedor{" "}
              <Highlight>{currentSupplierLabel}</Highlight> para o fornecedor{" "}
              <Highlight>{target?.supplierName}</Highlight>?
            </WarningHero>

            {target?.isNew ? (
              <WarningBox>
                O fornecedor selecionado ainda não faz parte desta forma de
                cobrança. Ao confirmar, ele será adicionado automaticamente à
                forma de cobrança <Highlight>{calcLabel}</Highlight> e receberá a
                categoria <Highlight>{categoryLabel}</Highlight>.
              </WarningBox>
            ) : (
              <InfoBox>
                A categoria permanece na forma de cobrança{" "}
                <Highlight>{calcLabel}</Highlight>. O fornecedor de origem
                continuará cadastrado no orçamento, mesmo que fique sem
                categorias.
              </InfoBox>
            )}

            <ConsequenceList items={CONSEQUENCES} />
          </>
        )}

        {(step === "mode" || step === "pick") && (
          <>
            <ModalHero icon={<MdPersonAddAlt1 size={26} />}>
              Transfira esta categoria para outro fornecedor da mesma forma de
              cobrança.
            </ModalHero>

            <SummaryBlock>
              <SummaryRow label="Categoria" value={categoryLabel} />
              <SummaryRow
                label="Fornecedor atual"
                value={currentSupplierLabel}
              />
              <SummaryRow label="Forma de cobrança" value={calcLabel} />
            </SummaryBlock>

            {step === "mode" && (
              <>
                <SectionLabel
                  text="Escolha o destino"
                  hint="O fornecedor de destino precisa estar na mesma forma de cobrança da categoria. Se ele ainda não estiver no orçamento, use 'Novo fornecedor'."
                />

                <ChoiceCard
                  selected={mode === "existing"}
                  disabled={!hasExistingSuppliers}
                  icon={<MdBusiness size={19} />}
                  title="Fornecedor existente"
                  description={
                    <>
                      Transferir para um fornecedor já cadastrado na forma de
                      cobrança atual (<Highlight>{calcLabel}</Highlight>).
                    </>
                  }
                  onSelect={() => setMode("existing")}
                />

                <ChoiceCard
                  selected={mode === "new"}
                  icon={<MdAdd size={20} />}
                  title="Novo fornecedor"
                  description="Adicionar um fornecedor ao orçamento nesta forma de cobrança e transferir a categoria."
                  onSelect={() => setMode("new")}
                />

                {!hasExistingSuppliers && (
                  <Typography
                    sx={{ fontSize: 12.5, color: COLORS.warnIcon, mt: 1 }}
                  >
                    Não existem outros fornecedores cadastrados nesta forma de
                    cobrança. Selecione "Novo fornecedor" para continuar.
                  </Typography>
                )}
              </>
            )}

            {step === "pick" && mode === "existing" && (
              <>
                <SectionLabel
                  text="Fornecedor de destino"
                  hint={`Somente fornecedores já cadastrados na forma de cobrança ${calcOption} são listados.`}
                />
                <Select
                  fullWidth
                  size="small"
                  value={existingId}
                  displayEmpty
                  onChange={(e) => setExistingId(e.target.value)}
                  inputProps={{ "aria-label": "Fornecedor de destino" }}
                  sx={{ borderRadius: "6px", fontSize: 13.5 }}
                >
                  <MenuItem value="" disabled>
                    Selecione um fornecedor
                  </MenuItem>
                  {existingSuppliers.map((supplier) => (
                    <MenuItem
                      key={supplier.supplierId}
                      value={supplier.supplierId}
                      sx={{ fontSize: 13.5 }}
                    >
                      {supplier.label}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {step === "pick" && mode === "new" && (
              <>
                <SectionLabel
                  text="Buscar na base de fornecedores"
                  hint="O fornecedor não precisa estar vinculado ao orçamento — ele será adicionado automaticamente nesta forma de cobrança."
                />
                <SupplierAutoComplete
                  title=""
                  label=""
                  provider_id=""
                  onSelectSupplier={() => undefined}
                  onSelectSupplierOption={(option) => setNewOption(option)}
                  // O fornecedor atual da categoria não pode ser o destino —
                  // exceto o "a definir", que existe justamente para ser
                  // repetido: cada linha reserva um fornecedor real distinto.
                  getOptionDisabled={(option: any) =>
                    option?._id === currentSupplierRealId &&
                    !isTemporarySupplierOption(option)
                  }
                />
                <Typography
                  sx={{ fontSize: 12.5, color: COLORS.muted, mt: 1.25 }}
                >
                  Pesquise por nome, razão social ou CNPJ.
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={modalActionsSx}>
        {step === "mode" && (
          <>
            <Button variant="outlined" onClick={handleClose} sx={cancelButtonSx}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!mode}
              onClick={() => setStep("pick")}
              sx={primaryButtonSx}
            >
              Avançar
            </Button>
          </>
        )}
        {step === "pick" && (
          <>
            <Button variant="outlined" onClick={handleClose} sx={cancelButtonSx}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!target}
              onClick={() => setStep("confirm")}
              sx={primaryButtonSx}
            >
              Avançar
            </Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={saving}
              sx={cancelButtonSx}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={saving}
              sx={primaryButtonSx}
            >
              {saving ? "Transferindo..." : "Transferir categoria"}
            </Button>
          </>
        )}
        {step === "done" && (
          <Button variant="contained" onClick={onClose} sx={primaryButtonSx}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferCategorySupplierModal;
